import { CommonModule } from '@angular/common';
import { Component, computed, forwardRef, input, linkedSignal, model, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cn } from '@cf/core';
import { cva, VariantProps } from 'class-variance-authority';

const colors = {
  blue: {
    bg: 'tw:bg-blue-10',
    handleBg: 'tw:bg-blue-50',
  },
  gray: {
    bg: 'tw:bg-gray-10',
    handleBg: 'tw:bg-white',
  },
  mint: {
    bg: 'tw:bg-mint-10',
    handleBg: 'tw:bg-mint-50',
  },
  green: {
    bg: 'tw:bg-green-10',
    handleBg: 'tw:bg-green-50',
  },
  purple: {
    bg: 'tw:bg-purple-1-new',
    handleBg: 'tw:bg-white',
  },
  yellow: {
    bg: 'tw:bg-yellow-20',
    handleBg: 'tw:bg-yellow-50',
  },
  acid: {
    bg: 'tw:bg-acid-10',
    handleBg: 'tw:bg-acid-50',
  },
  darkPink: {
    bg: 'tw:bg-dark-pink-10',
    handleBg: 'tw:bg-dark-pink-50',
  },
  violet: {
    bg: 'tw:bg-violet-10',
    handleBg: 'tw:bg-violet-50',
  },
  peach: {
    bg: 'tw:bg-peach-10',
    handleBg: 'tw:bg-peach-50',
  },
  disabled: {
    bg: 'tw:bg-gray-10',
    handleBg: 'tw:bg-gray-20',
  },
} as const;

export type SwitchColors = keyof typeof colors;

export type SwitchLabelPositions = 'before' | 'after';

const switchLabelVariants = cva(
  'tw:inline-flex tw:items-center tw:gap-2 tw:cursor-pointer tw:peer-disabled:cursor-not-allowed',
  {
    variants: {
      disabled: {
        true: ['tw:text-gray-40'],
        false: ['tw:text-gray-80'],
      },
      size: {
        xs: ['tw:text-xs tw:gap-1'],
        sm: ['tw:text-sm tw:gap-1'],
        md: ['tw:text-base tw:gap-2'],
        lg: ['tw:text-lg tw:gap-2'],
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

export type SwitchVariants = VariantProps<typeof switchLabelVariants>;
export type SwitchSizes = SwitchVariants['size'];

export interface SwitchChangeEvent {
  source: SwitchComponent;
  checked: boolean;
}

@Component({
  selector: 'cf-switch',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './switch.component.html',
  styleUrl: './switch.component.css',
  exportAs: 'cfSwitch',
  host: {
    class: 'tw:inline-flex tw:leading-none',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SwitchComponent),
      multi: true,
    },
  ],
})
export class SwitchComponent<T = any> implements ControlValueAccessor {
  id = input(`switch-${Math.random().toString(36).substring(2, 9)}`);

  checked = model(false);
  disabled = input(false);
  value = input<T | null>(null);
  // eslint-disable-next-line @angular-eslint/no-output-native
  change = output<SwitchChangeEvent>();
  size = input<SwitchSizes>('sm');
  labelClasses = input<string>('');
  color = input<SwitchColors>('blue');
  labelPosition = input<SwitchLabelPositions>('after');
  currentColor = computed(() => {
    if (this.disabled()) {
      return colors['disabled'];
    }
    return this.checked() ? colors[this.color()] : colors['gray'];
  });

  disabledState = linkedSignal(() => this.disabled());

  switchLabelClasses = computed(() =>
    cn(
      switchLabelVariants({
        disabled: this.disabled(),
        size: this.size(),
      }),
      this.labelClasses(),
    ),
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = (_value: boolean) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched = () => {};

  handleSwitchChange(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.disabled()) {
      return;
    }

    this.checked.set((event.target as HTMLInputElement).checked);
    this.onChange(this.checked());
    this.change.emit({
      source: this,
      checked: this.checked(),
    });
    this.onTouched();
  }

  handleSwitchBlur() {
    this.onTouched();
  }

  writeValue(value: boolean) {
    this.checked.set(value ?? false);
  }

  registerOnChange(fn: (value: boolean) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabledState.set(isDisabled);
  }
}
